/* eslint-disable ardoq-lint/no-hardcoded-strings --
   Dev-only internal authoring tool. None of this copy is customer-facing, so it
   stays inline English (no i18n). */
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, radius, shadows } from '@ardoq/design-tokens';
import { Box, FlexBox, Inline, Stack } from '@ardoq/layout';
import { ButtonSize, PrimaryButton, SecondaryButton } from '@ardoq/button';
import { Text } from '@ardoq/typography';
import { TopLayerPortal } from '../topLayerPortal';
import { type CapturedAnchor, describeAnchor } from './describeAnchor';

const Highlight = styled.div`
  position: fixed;
  pointer-events: none;
  border: 2px solid ${colors.brand500};
  border-radius: ${radius.small};
  box-shadow: ${shadows.shadowM};
  transition:
    top 0.05s ease,
    left 0.05s ease,
    width 0.05s ease,
    height 0.05s ease;
`;

const writeClipboard = (value: string): void => {
  // Clipboard can fail (permissions / insecure context); the on-screen list is
  // the fallback record, so this is non-fatal.
  navigator.clipboard?.writeText(value).catch(() => undefined);
};

const summarise = (anchor: CapturedAnchor): string => {
  const mark = anchor.needsAnchor ? '⚠︎' : '✓';
  const what = anchor.label || anchor.tag;
  const where = anchor.selector ?? 'needs anchor';
  return `${mark} ${what} — ${where}`;
};

const useTourAuthorOverlay = () => {
  const [isActive, setIsActive] = useState(false);
  const [capturedAnchors, setCapturedAnchors] = useState<CapturedAnchor[]>([]);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive) {
      setHoverRect(null);
      return;
    }
    const isOwnUi = (node: EventTarget | null): boolean =>
      node instanceof Node && Boolean(containerRef.current?.contains(node));

    const onMove = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || isOwnUi(target)) {
        setHoverRect(null);
        return;
      }
      setHoverRect(target.getBoundingClientRect());
    };

    const onClick = (event: MouseEvent) => {
      // Let clicks on our own toolbar (toggle / Copy all) behave normally.
      if (isOwnUi(event.target)) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      // Suppress the app's own click while picking.
      event.preventDefault();
      event.stopPropagation();
      const anchor = describeAnchor(target);
      setCapturedAnchors(previous => [...previous, anchor]);
      writeClipboard(JSON.stringify(anchor, null, 2));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsActive(false);
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isActive]);
  return {
    isActive,
    setIsActive,
    capturedAnchors,
    setCapturedAnchors,
    hoverRect,
    containerRef,
  };
};

/**
 * Capture tool for tour authors. Toggle pick mode, hover to highlight, click any
 * element to capture a loose anchor description (copied to clipboard and listed).
 * Reads only the live DOM, so it works in production. Rendered inline in the
 * assistant chat footer, directly above the text input (see ChatWindowBody). The
 * hover highlight is portaled into the top layer so it paints above every overlay
 * it can be pointed at, and stays viewport-aligned despite the chat window's
 * transformed ancestor (which would otherwise act as the containing block for a
 * `position: fixed` child).
 */
export const TourAuthorOverlay = () => {
  const {
    isActive,
    setIsActive,
    capturedAnchors,
    setCapturedAnchors,
    hoverRect,
    containerRef,
  } = useTourAuthorOverlay();

  return (
    <>
      {isActive && hoverRect && (
        <TopLayerPortal reassertKey="tour-author-highlight">
          <Highlight
            style={{
              top: hoverRect.top,
              left: hoverRect.left,
              width: hoverRect.width,
              height: hoverRect.height,
            }}
          />
        </TopLayerPortal>
      )}
      <FlexBox
        ref={containerRef}
        flexDirection="column"
        gap="small"
        paddingX="medium"
        paddingBottom="small"
      >
        {capturedAnchors.length > 0 && (
          <Box
            backgroundColor="surfaceDefault"
            borderWidth={1}
            borderColor="borderSubtle"
            borderRadius="medium"
            boxShadow="shadowM"
            padding="small"
            overflowY="auto"
            style={{ maxHeight: '30vh' }}
          >
            <Stack gap="small">
              <FlexBox justify="space-between" align="center" gap="small">
                <Text variant="text2Bold">{`Captured (${capturedAnchors.length})`}</Text>
                <Inline gap="small" align="center">
                  <SecondaryButton
                    buttonSize={ButtonSize.SMALL}
                    onClick={() =>
                      writeClipboard(JSON.stringify(capturedAnchors, null, 2))
                    }
                  >
                    Copy all
                  </SecondaryButton>
                  <SecondaryButton
                    buttonSize={ButtonSize.SMALL}
                    onClick={() => setCapturedAnchors([])}
                  >
                    Clear
                  </SecondaryButton>
                </Inline>
              </FlexBox>
              {capturedAnchors.map((anchor, index) => (
                <Text key={index} variant="text2" color="textSubtle">
                  {summarise(anchor)}
                </Text>
              ))}
            </Stack>
          </Box>
        )}
        <PrimaryButton onClick={() => setIsActive(previous => !previous)}>
          {isActive ? 'Stop picking' : 'Pick tour anchor 🎯'}
        </PrimaryButton>
      </FlexBox>
    </>
  );
};
